/* eslint-disable import/prefer-default-export */
import { FieldFunctionOptions, FieldPolicy, Reference } from '@apollo/client'
import { isNumber } from 'lodash'

import { FilterAssetsInput } from '~generated/types'
import { getNewIncomingData } from '~lib/apollo/entities/utils'

type PaginatedAssetsCacheObj = {
  docs?: Reference[]
  limit?: number
  page?: number
  nextPage?: number
  prevPage?: number
  hasNextPage?: boolean
  hasPrevPage?: boolean
  totalDocs?: number
  totalPages?: number
  pagingCounter?: number
}

type Existing = PaginatedAssetsCacheObj
type Incoming = PaginatedAssetsCacheObj

export function createPaginatedAssetsQueryCache(): FieldPolicy<Existing, Incoming> {
  return {
    keyArgs: () => {
      const filterKeysDict: KeysEnum<FilterAssetsInput> = {
        approverIds: true,
        ownerIds: true,
        tagIds: true,
        launchIds: true,
        types: true,
        approvalStatuses: true,
        isBrandAsset: true,
        isEmpty: true,
        createdAtLte: true,
        createdAtGte: true,
        query: true,
      }

      return ['filter', Object.keys(filterKeysDict), 'pagination', ['limit']]
    },

    merge: (existing: Existing, incoming: Incoming, { readField, canRead }: FieldFunctionOptions) => {
      const { docs: existingDocs = [] } = existing || {}
      const { docs: incomingDocs = [] } = incoming

      const newIncomingDocs = getNewIncomingData({
        incomingData: incomingDocs,
        existingData: existingDocs,
        readField,
      })

      return {
        ...existing,
        ...incoming,
        docs: [...existingDocs, ...newIncomingDocs].filter(canRead),
      }
    },

    read: (existing: Existing, { variables, readField, canRead }: FieldFunctionOptions) => {
      if (!existing) return undefined

      const existingDocs = [...(existing?.docs || [])].filter(canRead)

      // TODO - support all sorting value
      // TODO - implement asc and desc sorting
      if (isNumber(variables?.sort?.orderIndex)) {
        existingDocs.sort((a, b) => {
          const orderIndexA = readField('orderIndex', a)
          const orderIndexB = readField('orderIndex', b)

          if (orderIndexA === orderIndexB) return 0

          return orderIndexA > orderIndexB ? 1 : -1
        })
      }

      return {
        ...existing,
        docs: existingDocs,
      }
    },
  }
}
